import { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import isEqual from 'lodash.isequal';

import { isEmpty } from './converters';
import { useStorage } from './useStorage';
import { useOnMount } from '../useOnMount';
import { useUrlActions } from './useUrlActions';
import type { UseUrlParamParams } from './types';

const defaultParseData = <T>(data: any): T => data;

export const useParam = <T>({
  name,
  defaultValue,
  enableUrl = true,
  storageKey,
  storageType = 'session',

  parseData = defaultParseData,
}: UseUrlParamParams<T>) => {
  const history = useHistory();
  const location = useLocation();

  const [getFromStorage, setToStorage] = useStorage<T>({
    storageKey,
    storageType,
  });
  const [getInitialParam, setParamToUrl] = useUrlActions<T>({
    name,
    history,
    search: location.search,
  });

  const [param, setParam] = useState<T | undefined>(() => {
    const fromUrl = enableUrl ? getInitialParam() : null;
    return parseData(fromUrl ?? getFromStorage()) ?? defaultValue;
  });

  useOnMount(() => {
    if (!enableUrl || isEmpty(param)) return;
    if (!getFromStorage()) return;
    if (isEqual(getFromStorage(), getInitialParam())) return;

    setParamToUrl(param);
  });

  const handleParamChange = useCallback(
    (newParam?: T) => {
      setParam(newParam);
      setToStorage(newParam);
      enableUrl && setParamToUrl(newParam);
    },
    [enableUrl, setParamToUrl, setToStorage]
  );

  return [param, handleParamChange] as const;
};
